/**
 * Employee leave calendar.
 *
 * One page for every role:
 *  - Any staff member applies for leave (date range) against their accrued
 *    balance (2 days/month, Jan–Dec), sees their own requests, and can cancel a
 *    pending or future-approved one.
 *  - Colleagues in the same clinic appear on the month grid so leaves can be
 *    planned around each other. Admins see their clinics; superadmin sees all.
 *  - The superadmin gets an approvals queue to approve / reject pending requests.
 *
 * Colour key: amber = applied/pending, green = approved, red = rejected.
 */

import { useMemo, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  StatCard,
  Table,
  Td,
  Th,
} from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { leaveService } from '../../services';
import { formatDate, todayISO } from '../../utils/format';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_CELL = {
  PENDING: 'bg-amber-100 text-amber-800 ring-amber-600/20',
  APPROVED: 'bg-emerald-100 text-emerald-800 ring-emerald-600/20',
  REJECTED: 'bg-red-100 text-red-800 ring-red-600/20',
  CANCELLED: 'bg-ink-100 text-ink-500 ring-ink-400/20',
};
const STATUS_BADGE = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Month grid cells, Monday-first; nulls pad the leading/trailing week. */
function buildMonthCells(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const lead = (first.getDay() + 6) % 7; // convert Sun=0 to Mon=0
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, monthIndex, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function LeaveCalendar() {
  const { user, role } = useAuth();
  const isSuperadmin = role === 'SUPERADMIN';
  const myId = user?.id;

  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const monthStart = useMemo(() => ymd(new Date(cursor.year, cursor.month, 1)), [cursor]);
  const monthEnd = useMemo(
    () => ymd(new Date(cursor.year, cursor.month + 1, 0)),
    [cursor],
  );

  const { data: balance, reload: reloadBalance } = useApi(() => leaveService.balance(), []);
  const {
    data: monthLeaves,
    loading: calLoading,
    error: calError,
    reload: reloadMonth,
  } = useApi(
    () => leaveService.list({ from: monthStart, to: monthEnd }),
    [monthStart, monthEnd],
  );
  const { data: myLeaves, reload: reloadMine } = useApi(
    () => leaveService.list({ user_id: myId }),
    [myId],
  );
  const { data: pending, reload: reloadPending } = useApi(
    () => (isSuperadmin ? leaveService.list({ status: 'PENDING' }) : Promise.resolve([])),
    [isSuperadmin],
  );

  const [form, setForm] = useState({ start_date: todayISO(), end_date: todayISO(), reason: '' });
  const [applyBusy, setApplyBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  function reloadAll() {
    reloadBalance();
    reloadMonth();
    reloadMine();
    if (isSuperadmin) reloadPending();
  }

  async function submitApply(event) {
    event.preventDefault();
    setApplyBusy(true);
    setBanner(null);
    try {
      await leaveService.apply({
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || null,
      });
      setBanner({ tone: 'success', message: 'Leave request submitted for approval.' });
      setForm((f) => ({ ...f, reason: '' }));
      reloadAll();
    } catch (err) {
      setBanner({ tone: 'error', message: err.message });
    } finally {
      setApplyBusy(false);
    }
  }

  async function cancel(leaveId) {
    setBanner(null);
    try {
      await leaveService.cancel(leaveId);
      setBanner({ tone: 'success', message: 'Leave cancelled.' });
      reloadAll();
    } catch (err) {
      setBanner({ tone: 'error', message: err.message });
    }
  }

  async function review(leaveId, approve) {
    setBanner(null);
    try {
      await leaveService.review(leaveId, { approve });
      setBanner({ tone: 'success', message: approve ? 'Leave approved.' : 'Leave rejected.' });
      reloadAll();
    } catch (err) {
      setBanner({ tone: 'error', message: err.message });
    }
  }

  const cells = useMemo(() => buildMonthCells(cursor.year, cursor.month), [cursor]);
  const leavesByDay = useMemo(() => {
    const map = new Map();
    for (const lv of monthLeaves ?? []) {
      // expand the range across the days it covers in this month
      for (const cell of cells) {
        if (!cell) continue;
        const key = ymd(cell);
        if (key >= lv.start_date && key <= lv.end_date) {
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(lv);
        }
      }
    }
    return map;
  }, [monthLeaves, cells]);

  const todayStr = todayISO();

  function stepMonth(delta) {
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }

  return (
    <>
      <PageHeader
        title="Leave calendar"
        description="Apply for leave, track approvals, and see who else is off."
      />

      {banner && (
        <div className="mb-4">
          <Alert tone={banner.tone} title={banner.tone === 'error' ? 'Could not complete' : 'Done'} onDismiss={() => setBanner(null)}>
            {banner.message}
          </Alert>
        </div>
      )}

      {/* Balance */}
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <StatCard label={`Available (${balance?.year ?? ''})`} value={balance?.available ?? '—'} tone="positive" hint="days you can still take" />
        <StatCard label="Accrued so far" value={balance?.accrued ?? '—'} hint={`${balance?.per_month ?? 2}/month, resets in Jan`} />
        <StatCard label="Used / reserved" value={balance?.used ?? '—'} tone="brand" hint="pending + approved" />
      </div>

      {/* Apply */}
      <Card className="mb-5">
        <CardHeader title="Apply for leave" description="Full days, within one calendar year. Balance is reserved once you apply." />
        <form onSubmit={submitApply} className="grid gap-3 px-5 py-4 sm:grid-cols-4">
          <Field label="From" htmlFor="lv-from" required>
            <Input id="lv-from" type="date" min={todayStr} value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))} required />
          </Field>
          <Field label="To" htmlFor="lv-to" required>
            <Input id="lv-to" type="date" min={form.start_date} value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} required />
          </Field>
          <Field label="Reason (optional)" htmlFor="lv-reason">
            <Input id="lv-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Family function" />
          </Field>
          <div className="flex items-end">
            <Button type="submit" loading={applyBusy} className="w-full">Apply</Button>
          </div>
        </form>
      </Card>

      {/* Approvals queue (superadmin) */}
      {isSuperadmin && (
        <Card className="mb-5">
          <CardHeader title="Pending approvals" description="Approve or reject staff leave requests." />
          {(pending ?? []).length === 0 ? (
            <EmptyState title="Nothing to review" description="No leave requests are awaiting approval." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Employee</Th>
                  <Th>Clinic</Th>
                  <Th>Dates</Th>
                  <Th align="right">Days</Th>
                  <Th>Reason</Th>
                  <Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(pending ?? []).map((lv) => (
                  <tr key={lv.id} className="hover:bg-ink-50/60">
                    <Td className="font-medium text-ink-900">{lv.user_name}</Td>
                    <Td className="text-ink-600">{lv.clinic_name ?? '—'}</Td>
                    <Td>{formatDate(lv.start_date)} – {formatDate(lv.end_date)}</Td>
                    <Td align="right">{lv.days}</Td>
                    <Td className="max-w-[14rem] truncate text-ink-600">{lv.reason ?? '—'}</Td>
                    <Td align="right">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" onClick={() => review(lv.id, true)}>Approve</Button>
                        <Button size="sm" variant="secondary" onClick={() => review(lv.id, false)}>Reject</Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Month calendar */}
      <Card className="mb-5">
        <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="secondary" onClick={() => stepMonth(-1)}>‹</Button>
            <span className="min-w-[10rem] text-center font-semibold text-ink-900">
              {MONTHS[cursor.month]} {cursor.year}
            </span>
            <Button size="sm" variant="secondary" onClick={() => stepMonth(1)}>›</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
            <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm bg-amber-300" /> Applied</span>
            <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm bg-emerald-400" /> Approved</span>
            <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm bg-red-400" /> Rejected</span>
          </div>
        </div>

        {calError ? (
          <div className="p-5"><Alert tone="error" title="Could not load the calendar">{calError.message}</Alert></div>
        ) : calLoading ? (
          <div className="grid place-items-center py-16 text-brand-600"><Spinner size="lg" /></div>
        ) : (
          <div className="p-3">
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="px-1 py-1 text-center text-xs font-semibold uppercase tracking-wide text-ink-400">{w}</div>
              ))}
              {cells.map((cell, i) => {
                if (!cell) return <div key={`pad-${i}`} className="min-h-[72px] rounded-md bg-ink-50/40" />;
                const key = ymd(cell);
                const dayLeaves = leavesByDay.get(key) ?? [];
                const isToday = key === todayStr;
                return (
                  <div key={key} className={`min-h-[72px] rounded-md border p-1 ${isToday ? 'border-brand-400 bg-brand-50/40' : 'border-ink-100'}`}>
                    <div className={`mb-1 text-right text-xs ${isToday ? 'font-bold text-brand-700' : 'text-ink-500'}`}>{cell.getDate()}</div>
                    <div className="space-y-0.5">
                      {dayLeaves.slice(0, 3).map((lv) => (
                        <div
                          key={lv.id}
                          title={`${lv.user_name} · ${lv.status.toLowerCase()}${lv.reason ? ` · ${lv.reason}` : ''}`}
                          className={`truncate rounded px-1 py-0.5 text-[11px] font-medium ring-1 ring-inset ${STATUS_CELL[lv.status] ?? STATUS_CELL.CANCELLED}`}
                        >
                          {lv.user_id === myId ? 'You' : lv.user_name}
                        </div>
                      ))}
                      {dayLeaves.length > 3 && (
                        <div className="px-1 text-[10px] text-ink-400">+{dayLeaves.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* My requests */}
      <Card>
        <CardHeader title="My leave requests" description="Cancel a pending or upcoming approved leave to return the days to your balance." />
        {(myLeaves ?? []).length === 0 ? (
          <EmptyState title="No requests yet" description="Apply for leave above and it will appear here." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Dates</Th>
                <Th align="right">Days</Th>
                <Th>Status</Th>
                <Th>Reason</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {(myLeaves ?? []).map((lv) => {
                const canCancel = lv.status === 'PENDING'
                  || (lv.status === 'APPROVED' && lv.start_date > todayStr);
                return (
                  <tr key={lv.id} className="hover:bg-ink-50/60">
                    <Td className="text-ink-900">{formatDate(lv.start_date)} – {formatDate(lv.end_date)}</Td>
                    <Td align="right">{lv.days}</Td>
                    <Td>
                      <Badge tone={STATUS_BADGE[lv.status] ?? 'neutral'}>
                        {lv.status.charAt(0) + lv.status.slice(1).toLowerCase()}
                      </Badge>
                      {lv.status === 'REJECTED' && lv.review_note && (
                        <span className="ml-2 text-xs italic text-ink-500">{lv.review_note}</span>
                      )}
                    </Td>
                    <Td className="max-w-[14rem] truncate text-ink-600">{lv.reason ?? '—'}</Td>
                    <Td align="right">
                      {canCancel ? (
                        <Button size="sm" variant="secondary" onClick={() => cancel(lv.id)}>Cancel</Button>
                      ) : (
                        <span className="text-xs text-ink-400">—</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
